import { Stack } from '@mui/material';

import { QuestionnaireSectionLabel, QUESTIONNAIRE_PAGE_BOX_SX } from '../../../../common';
import {
  AutoSaveList,
  EMPTY_THERAPY_VALUES,
  NoTherapyCheckbox,
  NoTherapyConfirmDialog,
  TherapyFields,
  getTherapyDeleteMessage,
  getTherapyRowLabel,
  mapTherapyRowToValues,
  toTherapyFieldPayload,
  useFormReadOnly,
  useNoTherapyConfirm,
} from '../../../../../common';
import { THERAPY_FORM_SCHEMA } from './validationSchema';
import { useIntakeTherapies } from './hooks';
import { useIntakeQuestionnaireLoading } from '../../../hooks';
import { INTAKE_MONTH_YEAR_DISPLAY_FORMAT } from '../../../constants';
import { FORTA_BLUE } from '../../../../../../utils/colors';

import type { ITherapy, TherapyFormValues, TherapyPayload } from '../../../../../common';

export default function TherapyForm() {
  const {
    therapies,
    hasNoTherapies,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoTherapies,
  } = useIntakeTherapies();
  const isLoading = useIntakeQuestionnaireLoading();
  const readOnly = useFormReadOnly();
  const hasSavedTherapies = therapies.length > 0;
  const { isConfirmOpen, handleToggle, handleConfirm, closeConfirm } = useNoTherapyConfirm({
    hasSavedTherapies,
    clearAll,
    setNoTherapies,
  });

  return (
    <>
      <QuestionnaireSectionLabel variant="h5" sx={{ mb: 1.5, color: FORTA_BLUE.primary }}>
        Historical & present therapies
      </QuestionnaireSectionLabel>
      <Stack gap={2} textAlign="left" sx={QUESTIONNAIRE_PAGE_BOX_SX}>
        <QuestionnaireSectionLabel variant="h6">
          Child historical and present ABA and other therapies
        </QuestionnaireSectionLabel>
        <NoTherapyCheckbox
          checked={hasNoTherapies}
          disabled={isLoading || readOnly || isBulkSaving}
          onChange={handleToggle}
        />

        {hasNoTherapies ? null : (
          <AutoSaveList<TherapyFormValues, ITherapy, TherapyPayload>
            addButtonLabel="Add therapy"
            disabled={isLoading || isBulkSaving}
            emptyValues={EMPTY_THERAPY_VALUES}
            entries={therapies}
            getDeleteConfirm={(row) => ({
              title: 'Confirm therapy deletion?',
              message: getTherapyDeleteMessage(row),
            })}
            getRowKey={(row) => row.id}
            getRowLabel={getTherapyRowLabel}
            isSaving={isSaving}
            mapRowToValues={mapTherapyRowToValues}
            newEntryLabel="New therapy"
            onCreate={create}
            onDelete={remove}
            onUpdate={update}
            requiredMessage='Add at least one therapy or check "no historical therapy"'
            renderFields={({ formik, disabled, readOnly, saveField, backgroundColor }) => (
              <TherapyFields
                backgroundColor={backgroundColor}
                disabled={disabled}
                formik={formik}
                monthFormat={INTAKE_MONTH_YEAR_DISPLAY_FORMAT}
                readOnly={readOnly}
                saveField={saveField}
              />
            )}
            toFieldPayload={toTherapyFieldPayload}
            validationSchema={THERAPY_FORM_SCHEMA}
          />
        )}

        <NoTherapyConfirmDialog
          open={isConfirmOpen}
          hasSavedTherapies={hasSavedTherapies}
          confirmLoading={isBulkSaving}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />
      </Stack>
    </>
  );
}
